import { useId } from 'react'
import { useIntl } from 'react-intl'

import { PlusIcon, XIcon } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { InlineError } from '@/components/atoms/InlineError'
import { Sheet, SheetContent } from '@/components/atoms/Sheet/Sheet.tsx'
import { SupportConversation, type SupportConversationProps } from '@/components/organisms/SupportConversation'
import { SupportRequestForm, type SupportRequestFormProps } from '@/components/organisms/SupportRequestForm'
import { SupportRequestList, type SupportRequestListProps } from '@/components/organisms/SupportRequestList'
import { cn } from '@/lib/utils.ts'

export type SupportPanelContent =
  | {
      readonly view: 'requests'
      readonly centralName: string
      readonly onNewRequest: () => void
      readonly list: SupportRequestListProps
    }
  | { readonly view: 'loading' }
  | { readonly view: 'unavailable'; readonly onRetry: () => void }
  | { readonly view: 'new'; readonly form: SupportRequestFormProps }
  | { readonly view: 'conversation'; readonly conversation: SupportConversationProps }

export type SupportPanelProps = SupportPanelContent & { readonly onClose: () => void }

const PanelHeader = ({
  titleId,
  onClose,
  withDescription,
}: {
  titleId: string
  onClose: () => void
  withDescription: boolean
}) => {
  const intl = useIntl()
  return (
    <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 pt-5 pb-4">
      <div className="flex flex-col gap-1.5">
        <h2 id={titleId} className="font-heading text-lg font-semibold text-gray-900">
          {intl.formatMessage({ id: 'soutien.panel.title' })}
        </h2>
        {withDescription && (
          <p className="text-base leading-snug text-gray-500">
            {intl.formatMessage({ id: 'soutien.panel.description' })}
          </p>
        )}
      </div>
      <button
        type="button"
        aria-label={intl.formatMessage({ id: 'soutien.panel.close' })}
        onClick={onClose}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[4px] text-gray-700 hover:bg-gray-100">
        <XIcon aria-hidden className="size-[18px]" />
      </button>
    </header>
  )
}

export const SupportPanel = (props: SupportPanelProps) => {
  const intl = useIntl()
  const titleId = useId()

  switch (props.view) {
    case 'new':
      return <SupportRequestForm {...props.form} />
    case 'conversation':
      return <SupportConversation {...props.conversation} />
    case 'unavailable':
      return (
        <section aria-labelledby={titleId} className="flex h-full flex-col">
          <PanelHeader titleId={titleId} onClose={props.onClose} withDescription={false} />
          <InlineError
            layout="centered"
            message={intl.formatMessage({ id: 'soutien.unavailable.message' })}
            retryLabel={intl.formatMessage({ id: 'soutien.unavailable.retry' })}
            onRetry={props.onRetry}
          />
        </section>
      )
    case 'loading':
    case 'requests':
      return (
        <section aria-labelledby={titleId} className="flex h-full flex-col">
          <PanelHeader titleId={titleId} onClose={props.onClose} withDescription />
          {props.view === 'requests' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                <span className="flex flex-col gap-0.5">
                  <span className="font-bold text-gray-800">
                    {intl.formatMessage({ id: 'soutien.requests.title' })}
                  </span>
                  <span className="text-sm text-gray-600">{props.centralName}</span>
                </span>
                <Button variant="primary" size="sm" onClick={props.onNewRequest}>
                  <PlusIcon aria-hidden className="size-3.5 stroke-[2.5]" />
                  {intl.formatMessage({ id: 'soutien.requests.new' })}
                </Button>
              </div>
              <SupportRequestList {...props.list} />
            </>
          )}
        </section>
      )
  }
}

export interface SupportPanelSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly wide: boolean
  readonly embedded?: boolean
  readonly children: React.ReactNode
}

export const SupportPanelSheet = ({ open, onOpenChange, wide, embedded = false, children }: SupportPanelSheetProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      aria-describedby={undefined}
      overlayClassName={cn('bg-gray-900/30', embedded ? 'top-0' : 'top-[49px]')}
      className={cn('h-auto max-w-full', embedded ? 'top-0' : 'top-[49px]', wide ? 'w-[560px]' : 'w-[520px]')}>
      {children}
    </SheetContent>
  </Sheet>
)
